import { useEffect, useState } from "react";
import { ArrowUpRight, Briefcase, Wallet, Activity } from "lucide-react";
import api from "../../lib/api";

export function StatusPill({ status }) {
  const colors = {
    Pending: "text-slate-300 border-white/20 bg-white/5",
    "In Review": "text-sky-400 border-sky-400/30 bg-sky-400/10",
    "Proposal Sent": "text-amber-400 border-amber-400/30 bg-amber-400/10",
    "In Development": "text-volt border-volt/30 bg-volt/10",
    Completed: "text-green-400 border-green-400/30 bg-green-400/10",
  };
  return (
    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-3 py-1 ${colors[status] || colors.Pending}`}>
      {status}
    </span>
  );
}

export const money = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

export function SlotStatusPill({ status }) {
  const colors = {
    Booked: "text-slate-300 border-white/20 bg-white/5",
    Onboarded: "text-sky-400 border-sky-400/30 bg-sky-400/10",
    "In Progress": "text-amber-400 border-amber-400/30 bg-amber-400/10",
    Live: "text-volt border-volt/30 bg-volt/10",
  };
  return (
    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-3 py-1 ${colors[status] || colors.Booked}`}>
      {status}
    </span>
  );
}

export default function OverviewTab({ user, goTo }) {
  const [enquiries, setEnquiries] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/accounts/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const active = enquiries.filter((e) => e.status !== "Completed").length;

  const cards = [
    { icon: Briefcase, label: "Active Briefs", value: active, testid: "overview-active-briefs" },
    { icon: Wallet, label: "Account Balance", value: summary ? money(summary.balance) : "—", testid: "overview-balance" },
    { icon: Activity, label: "Ledger Entries", value: summary ? summary.count : "—", testid: "overview-entries" },
  ];

  return (
    <div data-testid="overview-tab" className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 border border-white/5">
        {cards.map((c) => (
          <div key={c.label} className="bg-obsidian p-8">
            <div className="flex items-center justify-between">
              <c.icon size={20} strokeWidth={1.5} className="text-volt" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">{user.zid}</span>
            </div>
            <p data-testid={c.testid} className="mt-8 font-display font-extrabold tracking-tight text-3xl">{c.value}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="border border-white/5 bg-panel/40 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-semibold text-xl tracking-tight">Latest Briefs</h3>
          <button data-testid="overview-new-brief-btn" onClick={() => goTo("briefs")} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-volt hover:underline">
            New Brief <ArrowUpRight size={14} />
          </button>
        </div>
        {enquiries.length === 0 ? (
          <p className="text-sm text-slate-500">No project briefs yet. Submit your first brief to get the machine running.</p>
        ) : (
          <div className="space-y-3">
            {enquiries.slice(0, 3).map((e) => (
              <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 border border-white/5 bg-obsidian px-5 py-4">
                <div>
                  <p className="font-medium text-sm">{e.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{e.service} · {e.budget}</p>
                </div>
                <StatusPill status={e.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
